import { useState, useEffect } from 'react';
import { useFormikContext } from 'formik';
import { useJobTimezoneHierarchy } from '@/jobs/presentation/hooks/useJobTimezoneHierarchy';
import { useListJobSitesAutocomplete, JobSiteAutocompleteOption } from '@/jobs/application/adapters/listJobSitesAutocomplete';
import { TypeaheadOption } from '@/common/presentation/components/inputs/Typeahead';
import { ERPJobFormValues } from '../../../ERPJobValidationSchema';
import { useCreateJobOptionalFieldsPreferences } from '@/raptorjob/presentation/hooks/useCreateJobOptionalFieldsPreferences';
import { JOB_FORM_MODES, type JobFormMode } from '@/raptorjob/domain/constants/jobFormLabels';

export type UseERPDailyDispatchTimesParams = {
  mode?: JobFormMode;
};

export const useERPDailyDispatchTimes = (params: UseERPDailyDispatchTimesParams = {}) => {
  const { mode = JOB_FORM_MODES.CREATE } = params;
  const { setFieldValue, setFieldTouched, errors, values } = useFormikContext<ERPJobFormValues>();
  const { visibility: optionalFieldsVisibility, setFieldVisible } = useCreateJobOptionalFieldsPreferences();

  const [showUpTimeActive, setShowUpTimeActive] = useState(
    mode === JOB_FORM_MODES.CREATE ? optionalFieldsVisibility.showShowUpTime || Boolean(values.showUpTime) : Boolean(values.showUpTime)
  );
  const [leaveByTimeActive, setLeaveByTimeActive] = useState(
    mode === JOB_FORM_MODES.CREATE ? optionalFieldsVisibility.showLeaveByTime || Boolean(values.leaveByTime) : Boolean(values.leaveByTime)
  );
  const [onJobTimeActive, setOnJobTimeActive] = useState(
    mode === JOB_FORM_MODES.CREATE ? optionalFieldsVisibility.showOnJobTime || Boolean(values.onJobTime) : Boolean(values.onJobTime)
  );

  const { options: jobSiteOptions } = useListJobSitesAutocomplete('', 1000, values.customerId || undefined);

  // Find selected jobSite to get coordinates
  const selectedJobSite: JobSiteAutocompleteOption | null =
    !values.jobSiteId || !jobSiteOptions.length
      ? null
      : (jobSiteOptions.find((option) => option.value === values.jobSiteId) as JobSiteAutocompleteOption) || null;

  // Get jobsite coordinates if available
  const jobsiteCoordinates =
    !selectedJobSite || selectedJobSite.latitude == null || selectedJobSite.longitude == null
      ? null
      : {
          latitude: selectedJobSite.latitude,
          longitude: selectedJobSite.longitude
        };

  // Note: jobTimezone uses values.timeZone which already contains jobData.timeZone in edit mode
  // (set in ERPJobCreateForm initialValues). For new jobs, it will be null/undefined.
  const { initialTimeZone, timezoneError, timezoneLoading, fetchTimeZonesWithInitial, handleTimezoneSelection } = useJobTimezoneHierarchy({
    rawJob: null, // rawJob is not available in ERP form context - timezone comes from values.timeZone instead
    preventUserTimezoneFallback: false,
    hierarchyContext: {
      jobTimezone: values.timeZone || undefined, // Use form value which contains jobData.timeZone in edit mode
      jobsiteCoordinates: jobsiteCoordinates, // Coordinates from selected jobSite if available
      userOverride: values.timeZone
    },
    useHierarchy: true
  });

  // Automatically activate fields when they have validation errors
  useEffect(() => {
    if (errors.leaveByTime) {
      setLeaveByTimeActive(true);
      setFieldTouched('leaveByTime', true, true);
    }
  }, [errors.leaveByTime, setFieldTouched]);

  useEffect(() => {
    if (errors.startTime) {
      setFieldTouched('startTime', true, true);
    }
  }, [errors.startTime, setFieldTouched]);

  useEffect(() => {
    if (errors.onJobTime) {
      setOnJobTimeActive(true);
      setFieldTouched('onJobTime', true, true);
    }
  }, [errors.onJobTime, setFieldTouched]);

  useEffect(() => {
    if (errors.showUpTime) {
      setShowUpTimeActive(true);
      setFieldTouched('showUpTime', true, true);
    }
  }, [errors.showUpTime, setFieldTouched]);

  const handleShowUpTimeRemove = () => {
    setShowUpTimeActive(false);
    setFieldValue('showUpTime', null);
    if (mode === JOB_FORM_MODES.CREATE) setFieldVisible('showShowUpTime', false);
  };

  const handleLeaveByTimeRemove = () => {
    setLeaveByTimeActive(false);
    setFieldValue('leaveByTime', null);
    if (mode === JOB_FORM_MODES.CREATE) setFieldVisible('showLeaveByTime', false);
  };

  const handleOnJobTimeRemove = () => {
    setOnJobTimeActive(false);
    setFieldValue('onJobTime', null);
    if (mode === JOB_FORM_MODES.CREATE) setFieldVisible('showOnJobTime', false);
  };

  const handleShowUpTimeAdd = () => {
    setShowUpTimeActive(true);
    if (mode === JOB_FORM_MODES.CREATE) setFieldVisible('showShowUpTime', true);
  };

  const handleLeaveByTimeAdd = () => {
    setLeaveByTimeActive(true);
    if (mode === JOB_FORM_MODES.CREATE) setFieldVisible('showLeaveByTime', true);
  };

  const handleOnJobTimeAdd = () => {
    setOnJobTimeActive(true);
    if (mode === JOB_FORM_MODES.CREATE) setFieldVisible('showOnJobTime', true);
  };

  const handleTimezoneChange = (selectedTimezone: TypeaheadOption<string> | null) => {
    if (selectedTimezone?.value) {
      setFieldValue('timeZone', selectedTimezone.value);
      if (handleTimezoneSelection) {
        handleTimezoneSelection(selectedTimezone);
      }
    } else {
      setFieldValue('timeZone', null);
      if (handleTimezoneSelection) {
        handleTimezoneSelection(null);
      }
    }
  };

  return {
    showUpTimeActive,
    setShowUpTimeActive,
    leaveByTimeActive,
    setLeaveByTimeActive,
    onJobTimeActive,
    setOnJobTimeActive,
    initialTimeZone,
    timezoneError,
    timezoneLoading,
    fetchTimeZonesWithInitial,
    handleShowUpTimeRemove,
    handleLeaveByTimeRemove,
    handleOnJobTimeRemove,
    handleShowUpTimeAdd,
    handleLeaveByTimeAdd,
    handleOnJobTimeAdd,
    handleTimezoneChange
  };
};
